import type { ApiProblem } from '@sazerac/design-tokens';

/**
 * Thin fetch wrapper for the public API.
 *
 * In development VITE_API_BASE_URL is empty and Vite proxies /api to the backend, so the
 * browser only ever sees one origin. In production it points at the deployed API.
 */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly problem: ApiProblem;

  constructor(status: number, problem: ApiProblem) {
    super(problem.detail || problem.title || 'Something went wrong.');
    this.name = 'ApiError';
    this.status = status;
    this.code = problem.code ?? 'error';
    this.fieldErrors = problem.errors ?? {};
    this.problem = problem;
  }

  /** First message for a field, matched case-insensitively against the API's casing. */
  fieldError(field: string): string | undefined {
    const key = Object.keys(this.fieldErrors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.fieldErrors[key]?.[0] : undefined;
  }

  get isNetwork() {
    return this.status === 0;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Skip JSON parsing (used for the .ics download). */
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, raw, headers, ...rest } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      headers: {
        Accept: raw ? '*/*' : 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // The request never reached the server: offline, DNS, or the API is not running.
    throw new ApiError(0, {
      code: 'network_error',
      title: 'Connection problem',
      detail: 'We could not reach the server. Check your connection and try again.',
    });
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let problem: ApiProblem = { status: response.status, detail: response.statusText };
    try {
      const parsed = await response.json();
      if (parsed && typeof parsed === 'object') problem = { ...problem, ...parsed };
    } catch {
      /* a non-JSON error body is fine; the status carries the meaning */
    }
    throw new ApiError(response.status, problem);
  }

  if (raw) return (await response.text()) as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, query }),
  put: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'PUT', body, query }),
  del: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { method: 'DELETE', query }),
};

/** Absolute URL for an API path, used for downloads the browser fetches directly. */
export function apiUrl(path: string, query?: RequestOptions['query']): string {
  return buildUrl(path, query);
}
